/**
 * One node in a schema.org graph. Nodes are kept as objects until the page is composed, so every
 * page ends up with a single `@graph` and no two nodes can share an `@id`.
 */
export interface JsonLdNode {
  "@type": string;
  "@id"?: string;
  [property: string]: unknown;
}

/**
 * Serialises a node graph for a `<script type="application/ld+json">` block.
 *
 * `<` is written as its unicode escape so text containing `</script>` cannot close the block early
 * and turn the rest of the page into markup. The parsed value is identical either way.
 *
 * This is not theoretical. `htmlToText` decodes entities, so a CMS field storing `&lt;/script&gt;`
 * reaches this point as a literal tag, and deal names come straight from affiliate feeds rather
 * than from an editor.
 */
export const toJsonLd = (value: unknown): string => JSON.stringify(value).replace(/</g, "\\u003c");

/** Wraps nodes in the one `@context` + `@graph` block a page emits. */
export const toJsonLdGraph = (nodes: JsonLdNode[]): string =>
  toJsonLd({ "@context": "https://schema.org", "@graph": nodes });
